<template>
  <div class="d-flex flex-column">
    <!--General-->
    <h6 class="section-title"><i class="fa-solid fa-building mr-50"></i>Identity</h6>
    <b-row>
      <b-col cols="12" md="6" xl="4" class="mb-1">
        <label>{{ $t('columns.name') }} <span class="text-danger">*</span></label>
        <b-form-input v-model="organization.name" :readonly="!canEditIdentity" :state="fieldState('name')"/>
        <b-form-invalid-feedback>{{ errors.name }}</b-form-invalid-feedback>
      </b-col>
      <b-col cols="6" md="3" xl="2" class="mb-1">
        <label>Acronym <span class="text-danger">*</span></label>
        <b-form-input v-model="organization.acronym" :readonly="!canEditIdentity" :state="fieldState('acronym')"/>
        <b-form-invalid-feedback>{{ errors.acronym }}</b-form-invalid-feedback>
      </b-col>
      <b-col cols="6" md="3" xl="2" class="mb-1">
        <label>Desk</label>
        <b-form-input v-model="organization.desk" list="org-desk-options" :readonly="!canEdit"/>
        <datalist id="org-desk-options">
          <option v-for="desk in deskOptions" :key="desk" :value="desk"/>
        </datalist>
      </b-col>
      <b-col cols="12" md="6" xl="4" class="mb-1">
        <label>{{ $t('columns.color') }}</label>
        <div class="d-flex align-items-center">
          <b-form-input v-model="organization.color" type="color" class="color-picker" :disabled="!canEdit"/>
          <b-form-input v-model="organization.color" class="ml-50" placeholder="#000000"
                        :readonly="!canEdit" :state="fieldState('color')"/>
          <b-button variant="outline-primary" class="ml-50 px-75" v-b-tooltip.hover title="Random color"
                    @click="organization.color = randomColor()" :disabled="!canEdit">
            <i class="fa-solid fa-dice"></i>
          </b-button>
          <b-button v-if="organization.color" variant="outline-secondary" class="ml-50 px-75"
                    v-b-tooltip.hover :title="$t('operations.clear')" @click="organization.color = null" :disabled="!canEdit">
            <i class="fa-solid fa-xmark"></i>
          </b-button>
        </div>
        <small v-if="errors.color" class="text-danger">{{ errors.color }}</small>
      </b-col>
      <b-col cols="12" md="6" class="mb-1">
        <label>ID</label>
        <b-input-group>
          <b-form-input :value="organization.id || '(assigned on save)'" readonly/>
          <b-input-group-append v-if="organization.id">
            <b-button variant="outline-secondary" v-b-tooltip.hover :title="$t('operations.copyId')"
                      @click="copy(organization.id)">
              <i class="fa-regular fa-copy"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col cols="12" md="6" class="mb-1">
        <label>Platform ID</label>
        <b-form-input v-model="organization.platformId" :readonly="!canEditIdentity"/>
      </b-col>
    </b-row>

    <h6 class="section-title"><i class="fa-solid fa-address-card mr-50"></i>Contact</h6>
    <b-row>
      <b-col cols="12" md="4" class="mb-1">
        <label>Contact name</label>
        <b-form-input v-model="organization.contactName" :readonly="!canEditIdentity"/>
      </b-col>
      <b-col cols="12" md="4" class="mb-1">
        <label>Phone</label>
        <b-input-group>
          <b-form-input v-model="organization.phone" :readonly="!canEditIdentity"/>
          <b-input-group-append>
            <b-button variant="outline-primary" :href="organization.phone ? `tel:${organization.phone}` : null"
                      :disabled="!organization.phone" v-b-tooltip.hover :title="$t('operations.call')">
              <i class="fa-solid fa-phone-volume"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col cols="12" md="4" class="mb-1">
        <label>{{ $t('columns.email') }}</label>
        <b-input-group>
          <b-form-input v-model="organization.email" type="email" :readonly="!canEditIdentity" :state="fieldState('email')"/>
          <b-input-group-append>
            <b-button variant="outline-primary" :href="organization.email ? `mailto:${organization.email}` : null"
                      :disabled="!organization.email" v-b-tooltip.hover :title="$t('operations.sendMail')">
              <i class="fa-solid fa-envelope"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small v-if="errors.email" class="text-danger">{{ errors.email }}</small>
      </b-col>
    </b-row>

    <h6 class="section-title"><i class="fa-solid fa-language mr-50"></i>Localization &amp; trading</h6>
    <b-row>
      <b-col cols="12" md="6" xl="3" class="mb-1">
        <label>Country</label>
        <countries-select-box v-model="organization.country" :read-only="!canEdit" :select-props="{ disabled: !canEdit }"/>
      </b-col>
      <b-col cols="12" md="6" xl="3" class="mb-1">
        <label>Language</label>
        <b-form-select v-model="organization.lang" :options="langOptions" :disabled="!canEdit"/>
      </b-col>
      <b-col cols="12" md="6" xl="3" class="mb-1">
        <label>Currency</label>
        <currencies-select-box v-model="organization.currency" :allowed-currencies="['EUR','USD']"
                               :read-only="!canEdit" :select-props="{ disabled: !canEdit }"/>
      </b-col>
      <b-col cols="12" md="6" xl="3" class="mb-1">
        <label>Default trade currency</label>
        <currencies-select-box v-model="organization.defaultTradeCurrency" :allowed-currencies="['EUR','USD','AUD']"
                               :read-only="!canEdit" :select-props="{ disabled: !canEdit }"/>
      </b-col>
      <b-col cols="12" md="6" xl="3" class="mb-1">
        <label>Telegram transactions group</label>
        <b-input-group>
          <b-input-group-prepend is-text><i class="fa-brands fa-telegram"></i></b-input-group-prepend>
          <b-form-input v-model="organization.telegramTransactionsGroup" placeholder="-1001234567890" :readonly="!canEdit"/>
        </b-input-group>
      </b-col>
    </b-row>

    <!-- test notification button - only for specific org -->
    <div v-if="organization.id === '67abb4ecfeb4fe34a15f9fa8'" class="mt-50">
      <b-button variant="outline-info" size="sm" :disabled="sendingTestNotification" @click="sendTestNotif">
        <i class="fa-solid fa-bell"></i>
        {{ sendingTestNotification ? 'Sending...' : 'Send Test Notification' }}
      </b-button>
    </div>

    <!--Websites-->
    <h6 class="section-title"><i class="fa-solid fa-globe mr-50"></i>Websites</h6>
    <b-row>
      <b-col v-for="field in urlFields" :key="field.key" cols="12" :md="field.wide ? 12 : 6" class="mb-1">
        <label>{{ field.label }}</label>
        <b-input-group>
          <b-form-input v-model="organization[field.key]" :placeholder="field.placeholder" :readonly="!canEdit"/>
          <b-input-group-append v-if="field.openable">
            <b-button variant="outline-secondary" :href="toUrl(organization[field.key])" target="_blank"
                      rel="noopener" :disabled="!organization[field.key]" v-b-tooltip.hover title="Open">
              <i class="fa-solid fa-arrow-up-right-from-square"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
        <small v-if="field.hint" class="text-muted">{{ field.hint }}</small>
      </b-col>
    </b-row>

    <!--Domains-->
    <h6 class="section-title">
      <i class="fa-solid fa-shield-halved mr-50"></i>Domains
      <b-badge pill variant="light-primary" class="ml-25">{{ domainCount }}</b-badge>
    </h6>
    <div v-for="field in domainFields" :key="field.key" class="mb-2">
      <label class="d-block">{{ field.label }} <small class="text-muted">— {{ field.hint }}</small></label>
      <b-form-tags v-model="organization[field.key]" :placeholder="field.placeholder"
                   :disabled="!canEditDomains" :tag-validator="isValidDomain"
                   separator=" ,;" add-button-text="Add" remove-on-delete
                   invalid-tag-text="Invalid domain" duplicate-tag-text="Already added"/>
    </div>

    <!--Payments-->
    <h6 class="section-title">
      <i class="fa-solid fa-credit-card mr-50"></i>Payments
      <b-badge pill variant="light-primary" class="ml-25">{{ processors.length }}</b-badge>
    </h6>
    <div v-if="processors.length === 0" class="text-center text-muted py-3">No payment processors configured</div>
    <b-row v-else>
      <b-col v-for="(p, i) in processors" :key="`${p.processorName}-${i}`" cols="12" md="6" class="mb-1">
        <b-card no-body class="border mb-0">
          <b-card-body class="p-1">
            <div class="d-flex align-items-center mb-50">
              <strong>{{ p.processorName }}</strong>
              <b-badge class="ml-50" :variant="p.active ? 'light-success' : 'light-secondary'">
                {{ p.active ? 'Active' : 'Inactive' }}
              </b-badge>
              <b-badge v-if="p.testMode" class="ml-25" variant="light-warning">Test mode</b-badge>
            </div>
            <div v-for="(value, key) in (p.params || {})" :key="key" class="small d-flex">
              <span class="text-muted mr-50">{{ key }}</span>
              <code class="ml-auto">{{ maskSecret(value) }}</code>
            </div>
            <div v-if="p.returnUrl" class="small text-truncate mt-50" :title="p.returnUrl">
              <span class="text-muted">Return:</span> {{ p.returnUrl }}
            </div>
            <div v-if="p.webhookBase" class="small text-truncate" :title="p.webhookBase">
              <span class="text-muted">Webhook:</span> {{ p.webhookBase }}
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <small class="text-muted">Payment processors are read-only here. Credentials are masked.</small>

    <!--Footer-->
    <div class="org-footer d-flex align-items-center border-top py-1 mt-1">
      <span v-if="dirty" class="text-warning small"><i class="fa-solid fa-circle fa-xs mr-25"></i>Unsaved changes</span>
      <span v-else-if="hasErrors" class="text-danger small">Fix the highlighted fields</span>
      <b-button class="ml-auto" variant="outline-secondary" @click="$emit('cancel')" :disabled="busy">
        {{ $t('operations.cancel') }}
      </b-button>
      <b-button class="ml-1" variant="primary" @click="save" :disabled="busy || hasErrors || (!newOrganization && !dirty)">
        <b-spinner v-if="busy" small class="mr-50"/>
        {{ newOrganization ? $t('operations.create') : $t('operations.save') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import Organization from '@/model/organization/Organization';
import { mapActions, mapGetters } from 'vuex';
import CountriesSelectBox from '@/components/widget/select/CountriesSelectBox.vue'
import CurrenciesSelectBox from '@/components/widget/select/CurrenciesSelectBox.vue'

const DOMAIN_FIELDS = ['allowedDomains', 'vipAllowedDomains', 'hostnames']

export default {
  name: 'OrganizationEditDetails',
  components: { CurrenciesSelectBox, CountriesSelectBox },
  mixins: [mixinPermissions, mixinNotifications],
  data: () => ({
    organization: Organization(),
    original: '',
    busy: false,
    sendingTestNotification: false,
    urlFields: [
      { key: 'website', label: 'Website', placeholder: 'https://example.com', openable: true },
      { key: 'vipWebsite', label: 'VIP website', placeholder: 'https://vip.example.com', openable: true },
      { key: 'crm', label: 'CRM URL', placeholder: 'https://example.crmanex.com/', openable: true },
      { key: 'websiteHealthCheckUrl', label: 'Health check URL', placeholder: 'https://example.com/account/api/connection/health', openable: true },
      { key: 'accountPath', label: 'Account login path', wide: true, placeholder: 'https://example.com/account/login?token={token}&id={id}', hint: '{token} and {id} are replaced when the client logs in' },
      { key: 'accountVipPath', label: 'VIP account login path', wide: true, placeholder: 'https://vip.example.com/account/login?token={token}&id={id}' },
    ],
    domainFields: [
      { key: 'allowedDomains', label: 'Allowed domains', hint: 'websites allowed to call the API', placeholder: 'example.com, account.example.com' },
      { key: 'vipAllowedDomains', label: 'VIP allowed domains', hint: 'VIP websites allowed to call the API', placeholder: 'vip.example.com' },
      { key: 'hostnames', label: 'CRM hostnames', hint: 'CRM hosts that resolve to this organization', placeholder: 'example.crmanex.com' },
    ],
  }),
  props: {
    value: {
      required: true,
      default: () => ({})
    },
    newOrganization: {
      type: Boolean,
      default: () => false
    }
  },
  watch: {
    value: {
      handler() {
        this.organization = Object.assign(Organization(), this.value)
        DOMAIN_FIELDS.forEach(k => { this.organization[k] = [...(this.organization[k] || [])] })
        this.original = JSON.stringify(this.normalized())
      },
      immediate: true
    },
    dirty: {
      handler(val) { this.$emit('dirty', val) },
      immediate: true
    },
  },
  computed: {
    ...mapGetters('data', ['allOrganizations']),
    canEdit() {
      return this.hasPermission(this.permissions.CLIENT_UPDATE_CONTACT)
    },
    // Identity and contact fields can only be set when the organization is created
    canEditIdentity() {
      return this.canEdit && this.newOrganization
    },
    canEditDomains() {
      return this.hasPermission(this.permissions.ORG_UPDATE)
    },
    dirty() {
      return JSON.stringify(this.normalized()) !== this.original
    },
    processors() {
      return this.organization.paymentProcessors || []
    },
    domainCount() {
      return DOMAIN_FIELDS.reduce((sum, k) => sum + (this.organization[k] || []).length, 0)
    },
    deskOptions() {
      return [...new Set((this.allOrganizations || []).map(o => o.desk).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    },
    langOptions() {
      const langs = ['en', 'fr']
      if (this.organization.lang && !langs.includes(this.organization.lang)) {
        langs.push(this.organization.lang)
      }
      return langs.map(l => ({ value: l, text: l.toUpperCase() }))
    },
    errors() {
      const o = this.organization
      const errors = {}
      if (!(o.name || '').trim()) errors.name = 'Name is required'
      if (!(o.acronym || '').trim()) errors.acronym = 'Acronym is required'
      if (o.color && !/^#[0-9a-f]{6}$/i.test(o.color)) errors.color = 'Use a hex color like #1A2B3C'
      if (o.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(o.email.trim())) errors.email = 'Invalid email'
      return errors
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0
    },
  },
  methods: {
    ...mapActions('organizations', ['createOrganization', 'updateOrganization', 'sendTestNotification']),

    fieldState(key) {
      return this.errors[key] ? false : null
    },
    randomColor() {
      return '#' + Math.floor(Math.random() * 0xFFFFFF).toString(16).padStart(6, '0');
    },
    isValidDomain(tag) {
      return /^[a-z0-9.-]+(:\d+)?$/i.test(tag.trim())
    },
    toUrl(value) {
      if (!value) return null
      const v = String(value).trim()
      return /^https?:\/\//i.test(v) ? v : `https://${v}`
    },
    maskSecret(value) {
      const v = String(value == null ? '' : value)
      return v.length <= 8 ? '••••' : `••••${v.slice(-4)}`
    },
    copy(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.showSuccessNotification(this.$t('globalMessages.copySuccess'))
      })
    },
    // Trims strings and cleans domain lists (stray spaces break domain matching on the backend)
    normalized() {
      const o = { ...this.organization }
      Object.keys(o).forEach(k => {
        if (typeof o[k] === 'string') {
          o[k] = o[k].trim() || null
        }
      })
      DOMAIN_FIELDS.forEach(k => {
        o[k] = [...new Set((o[k] || []).map(d => String(d).trim().toLowerCase()).filter(Boolean))]
      })
      return o
    },
    errorMessage(e) {
      return e?.response?.data?.error || e?.message
    },

    async save() {
      if (this.hasErrors) return
      this.busy = true
      const organization = this.normalized()
      try {
        if (this.newOrganization) {
          await this.createOrganization(organization)
          this.showSuccessNotification(this.$t('globalMessages.createDataSuccess', { system: organization.name }))
          // Organizations are loaded once with the user metadata, reload so the whole app sees the new one
          window.location.reload()
          return
        }
        await this.updateOrganization({ id: organization.id, organization })
        this.original = JSON.stringify(organization)
        this.showSuccessNotification(this.$t('globalMessages.updateDataSuccess', { system: organization.name }))
        this.$emit('saved', organization)
      } catch (e) {
        this.showErrorNotification(this.newOrganization ? 'Error creating organization' : 'Error updating organization', this.errorMessage(e))
      } finally {
        this.busy = false
      }
    },

    async sendTestNotif() {
      try {
        this.sendingTestNotification = true;
        await this.sendTestNotification('67abb4edfeb4fe34a15f9faf');
        this.showSuccessNotification('Test notification sent');
      } catch (e) {
        this.showErrorNotification('Failed to send test notification', this.errorMessage(e));
      } finally {
        this.sendingTestNotification = false;
      }
    },
  }
};
</script>

<style scoped>
.section-title {
  text-transform: uppercase;
  font-size: .75rem;
  letter-spacing: .05em;
  color: #b9b9c3;
  margin: 1.25rem 0 .75rem;
  padding-bottom: .4rem;
  border-bottom: 1px solid rgba(185, 185, 195, .3);
}

.section-title:first-child {
  margin-top: 0;
}

/* Keeps save/cancel visible while the modal body scrolls */
.org-footer {
  position: sticky;
  bottom: -0.8rem;
  z-index: 2;
  background: #fff;
}

.dark-layout .org-footer {
  background: #283046;
}

.color-picker {
  flex: 0 0 46px;
  height: 38px;
  padding: 2px;
  cursor: pointer;
}
</style>
